import type {
  MetaFieldType,
  MetaSchemaField,
} from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import { isReward } from "../../../lib/reward";
import type {
  GiftChannel,
  GiftType,
  GuildRecommendationsConfig,
  HelpClaimMode,
  HelpRequestType,
  InviterRewardStep,
  InvitesConfig,
  ProjectConfig,
} from "./types";

const META_FIELD_TYPES: MetaFieldType[] = ["string", "string[]", "int", "bool"];

/** What `normalizeMetaSchema` reads beyond `{key, type, allowed}`. `required`
 *  belongs to one schema and is meaningless in the other — see
 *  `MetaSchemaField.required` — so the caller says whether to keep it. */
interface NormalizeSchemaOptions {
  required?: boolean;
}

const CLAIM_MODES: HelpClaimMode[] = ["all_at_once", "one_at_a_time"];

/**
 * Every channel a gift may be sent through, in the order the panel draws them
 * — narrowest audience first, which is also the order a project widens through
 * as it gains confidence in the mechanic.
 *
 * This list is the allow-list's alphabet: the normalizer keeps what is in it
 * and drops the rest, and the checkbox row renders straight off it, so there is
 * one place a fourth channel would be added.
 */
export const GIFT_CHANNELS: GiftChannel[] = ["friends", "guild", "code"];

/**
 * Coerce a `{key, type, allowed?, required?}` schema into well-formed entries —
 * `profile.meta_schema`, and the `payload_schema` of a help request type, which
 * is the same declaration read by the same rules. Unlike the other schema
 * arrays this one is nested (`allowed` is itself a list), and the blob is
 * free-form JSONB the backend never rewrites — so a hand-edited config can
 * carry junk that would otherwise crash the editor. Entries without a usable
 * type are dropped; a malformed `allowed` degrades to "no allow-list".
 */
export function normalizeMetaSchema(
  input: unknown,
  opts?: NormalizeSchemaOptions,
): MetaSchemaField[] {
  if (!Array.isArray(input)) return [];
  const out: MetaSchemaField[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Partial<MetaSchemaField>;
    if (typeof entry.key !== "string") continue;
    if (!META_FIELD_TYPES.includes(entry.type as MetaFieldType)) continue;
    const field: MetaSchemaField = {
      key: entry.key,
      type: entry.type as MetaFieldType,
      allowed: Array.isArray(entry.allowed)
        ? entry.allowed.filter((v): v is string => typeof v === "string")
        : [],
    };
    // Only for the schema that READS it, and only when it is TRUE. A stored
    // `false` is dropped because the server marshals it away in the first place
    // (`card.Field.Required` is `omitempty`), so writing one back would be a
    // diff on a schema nobody touched; and a `required` sitting in a
    // `profile.meta_schema` is dropped because nothing reads it there and no
    // editor shows it — carried through, it would be an invisible key an
    // operator could neither see nor clear, which is the thing
    // MetaSchemaField.required exists to argue against.
    if (opts?.required && entry.required === true) field.required = true;
    out.push(field);
  }
  return out;
}

/**
 * Coerce `help_request.types` into rows of `{ type, claim }`, reading BOTH
 * forms the key can hold.
 *
 * Live projects store the list as bare strings, and a bare `"cards"` reads as
 * `{ type: "cards", claim: "all_at_once" }` — exactly the behaviour the
 * platform had before the mode existed, so the upgrade changes nobody's until
 * a designer picks "one at a time" themselves. An unrecognised `claim`
 * degrades the same way rather than refusing the whole row. Junk entries are
 * dropped, as the flat-string normalizer already dropped non-strings; the
 * panel writes the object form back on its first save.
 *
 * The quota pair (`max_per_window` / `window_seconds`) is read only as whole
 * positive numbers, and a key that isn't there STAYS not there — see `quota`.
 * A bare string carries no quota, so `"cards"` still reads as an unlimited
 * type. `donor_reward` is read by the same "as the server reads it, or not at
 * all" rule, over its own shape rather than over numbers.
 */
export function normalizeHelpTypes(input: unknown): HelpRequestType[] {
  if (!Array.isArray(input)) return [];
  const out: HelpRequestType[] = [];
  for (const raw of input) {
    if (typeof raw === "string") {
      // A stored empty name is junk, not a row the operator is midway through
      // typing: keeping it would trip the save-bar's "every type needs a name"
      // on a page nobody has edited yet.
      if (!raw) continue;
      out.push({ type: raw, claim: "all_at_once" });
      continue;
    }
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Partial<HelpRequestType>;
    if (typeof entry.type !== "string" || !entry.type) continue;
    const row: HelpRequestType = {
      type: entry.type,
      claim: CLAIM_MODES.includes(entry.claim as HelpClaimMode)
        ? (entry.claim as HelpClaimMode)
        : "all_at_once",
    };
    // Assigned, never defaulted: a type nobody limited must come back out of
    // the normalizer byte-for-byte as it went in, or the save bar reports a
    // change on a page the operator only looked at.
    //
    // A half pair survives on purpose. It is the one state the editor paints
    // red, and dropping the half that IS set would quietly rewrite what a
    // hand-edited config says instead of showing it.
    const max = quota(entry.max_per_window);
    if (max !== undefined) row.max_per_window = max;
    const window = quota(entry.window_seconds);
    if (window !== undefined) row.window_seconds = window;
    // The cap is read by the same rule and for the same reason as the quota: a
    // type the designer capped nowhere must come back out byte-for-byte as it
    // went in, or the save bar reports a change on a page nobody edited.
    const amount = quota(entry.max_amount);
    if (amount !== undefined) row.max_amount = amount;
    // The TTL parts company with the cap on exactly one value. `0` is not a
    // number the server ignores — it is one it ACTS on, "never expires" — so
    // dropping it here would show a row that runs forever as one running on the
    // section default, and save it as that.
    const ttl = ttlSeconds(entry.ttl_seconds);
    if (ttl !== undefined) row.ttl_seconds = ttl;
    // The donor's reward is read exactly as the server reads it: an object or
    // a list is one, and a scalar, a `null` or a missing key are all no reward
    // (lib/reward.ts, and internal/help/config.go parseDonorReward). A stored
    // `600` is therefore DROPPED, and the box comes up empty.
    //
    // That is the `quota` rule above, not the half-a-pair rule: what survives
    // being read is a value the server would act on, and what does not is a
    // value of the wrong shape for its own key — a `600` here is a `2.5` in a
    // quota key, not a max waiting for its window. Keeping it would put a
    // reward in the box that the platform hands nobody, which is worse than
    // showing the truth: this type pays nothing until somebody writes a reward
    // the server can read.
    if (isReward(entry.donor_reward)) row.donor_reward = entry.donor_reward;
    // Absent stays absent, for the reason the quota keys do — and here it is
    // the one that matters most: an empty schema and no schema are the same
    // thing to the server (both accept any payload), but writing `[]` back
    // into a config nobody edited is a diff on every project on the page.
    const schema = payloadSchema(entry.payload_schema);
    if (schema !== undefined) row.payload_schema = schema;
    out.push(row);
  }
  return out;
}

/**
 * Read a type's `payload_schema`, or answer `undefined` for a type that has
 * none — a stored value that is not a list at all, and a list with nothing
 * readable left in it. Both are what the server makes of them: an unreadable
 * schema is dropped (`parsePayloadSchema`) and a schema declaring no field the
 * platform can check is universal (`card.PayloadCatalogue`), so in either case
 * the type goes on accepting any payload.
 *
 * An emptied list therefore leaves NO key behind, which is also what the row
 * editor writes when the last field is deleted (`patchSchema`) — one state,
 * spelled one way, wherever it is arrived at.
 *
 * The fields themselves are read by `normalizeMetaSchema`: it is the same
 * declaration, and a second copy of "which types may a field declare" is a
 * second place for the answer to drift from the server's.
 */
function payloadSchema(input: unknown): MetaSchemaField[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const fields = normalizeMetaSchema(input, { required: true });
  return fields.length > 0 ? fields : undefined;
}

/** Every optional number on a type row reads the same way — whole, positive,
 *  or not there at all. That is the rule the server applies to each of them
 *  (`positiveInt`), and the panel has to answer a stored value the same way. */
function quota(v: unknown): number | undefined {
  return typeof v === "number" && Number.isInteger(v) && v > 0 ? v : undefined;
}

/**
 * A number whose `0` is a MEANING rather than an absence — "no threshold" —
 * so it survives where `quota` would drop it, and a negative comes back
 * as the same `0` the server reads it as. Its own reader rather than a reuse of
 * `ttlSeconds` below: that one's zero is a rule about TIME, and one function
 * for both would make the next change to either a change to both.
 */
function wholeCount(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isInteger(v)) return undefined;
  return v > 0 ? v : 0;
}

/** Any of the section's three durations, read the way the server reads one
 *  (`internal/help/config.go` ttlSetting): above zero it is a duration, at or
 *  below zero it is "never expires", and anything that is not a whole number is
 *  not a duration at all — the caller decides what "not a duration" falls back
 *  to, which is the one thing the three keys do differently. Non-positive comes
 *  back as the `0` the ∞ toggle itself writes, so the section has one spelling
 *  of "never" however it was hand-edited. */
function ttlSeconds(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isInteger(v)) return undefined;
  return v > 0 ? v : 0;
}

/**
 * Coerce `gift.channels` into the allow-list the panel can draw.
 *
 * Anything that is not one of the three known channels is dropped: the server
 * checks the recipient against the names it knows, so a stored `"clan"` allows
 * nobody, and showing it as a fourth box would promise a channel that does not
 * exist. A repeated name collapses — the key is a set, not a sequence.
 *
 * The result is in the panel's own order rather than the stored one, so two
 * configs that allow the same channels are ONE config: the save bar diffs
 * through stable JSON, and `["guild","friends"]` against `["friends","guild"]`
 * would otherwise read as an edit forever. Normalised on both sides of that
 * diff, so re-spelling is not an edit either — it rides along with the
 * operator's next save.
 */
export function normalizeGiftChannels(input: unknown): GiftChannel[] {
  if (!Array.isArray(input)) return [];
  const stored = new Set(input.filter((v): v is string => typeof v === "string"));
  return GIFT_CHANNELS.filter((channel) => stored.has(channel));
}

/**
 * Coerce `gift.types` into rows of `{ type, claim }` plus whatever quotas were
 * really set. The rules are `normalizeHelpTypes`', for the same reasons: an
 * unrecognised `claim` degrades to the default rather than losing the row, junk
 * entries are dropped, and a key that isn't there STAYS not there so a type
 * nobody limited comes back out byte-for-byte as it went in.
 *
 * The one rule it does NOT borrow is the bare-string form. Help reads
 * `"cards"` because live projects stored their list that way before the mode
 * existed; `gift.types` ships as objects and never had another form, so
 * inventing a second one here would be the panel supporting a config the
 * platform cannot produce.
 *
 * Both quota pairs are read half by half, and a half survives on purpose: it is
 * the one state the editor paints red, and dropping the half that IS set would
 * quietly rewrite what a hand-edited config says instead of showing it.
 */
export function normalizeGiftTypes(input: unknown): GiftType[] {
  if (!Array.isArray(input)) return [];
  const out: GiftType[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Partial<GiftType>;
    if (typeof entry.type !== "string" || !entry.type) continue;
    const row: GiftType = {
      type: entry.type,
      claim: CLAIM_MODES.includes(entry.claim as HelpClaimMode)
        ? (entry.claim as HelpClaimMode)
        : "all_at_once",
    };
    const max = quota(entry.max_per_window);
    if (max !== undefined) row.max_per_window = max;
    const window = quota(entry.window_seconds);
    if (window !== undefined) row.window_seconds = window;
    const amount = quota(entry.max_amount);
    if (amount !== undefined) row.max_amount = amount;
    // The per-recipient pair is read by exactly the rule its sibling above is:
    // one quota narrowed to one pair of players is still a quota, and a second
    // reading of "what is a count" would be a second thing to keep in step.
    const pairMax = quota(entry.pair_max_per_window);
    if (pairMax !== undefined) row.pair_max_per_window = pairMax;
    const pairWindow = quota(entry.pair_window_seconds);
    if (pairWindow !== undefined) row.pair_window_seconds = pairWindow;
    out.push(row);
  }
  return out;
}

/**
 * Coerce `guild.recommendations` into a well-formed key pair. Same reasoning
 * as normalizeMetaSchema: the sub-key may be missing entirely (every config
 * written before this feature existed), and a hand-edited blob can carry a
 * number, null or an array where a key belongs. Anything that isn't a string
 * degrades to "" — "criterion not configured".
 */
export function normalizeRecommendations(
  input: unknown,
): GuildRecommendationsConfig {
  const raw = (input ?? {}) as Partial<
    Record<keyof GuildRecommendationsConfig, unknown>
  >;
  return {
    requirement_guild_key:
      typeof raw.requirement_guild_key === "string"
        ? raw.requirement_guild_key
        : "",
    requirement_player_key:
      typeof raw.requirement_player_key === "string"
        ? raw.requirement_player_key
        : "",
  };
}

/**
 * Coerce the `invites` section — missing from every config written before the
 * feature existed, which is the state the defaults describe: off, no trigger,
 * no reward.
 *
 * The invitee's reward is read by the server's own rule (lib/reward.ts): an
 * object or a list is one, and a scalar, a `null` or a missing key are all no
 * reward — which is an ABSENT key rather than an empty one, so a project that
 * pays nobody comes back out of here byte-for-byte as it went in. A stored
 * `600` is therefore dropped and the box comes up empty, exactly as a help
 * type's `donor_reward` is.
 */
export function normalizeInvites(input: unknown): ProjectConfig["invites"] {
  const raw = (input ?? {}) as Partial<Record<keyof InvitesConfig, unknown>>;
  const out: ProjectConfig["invites"] = {
    enabled:
      typeof raw.enabled === "boolean"
        ? raw.enabled
        : DEFAULT_CONFIG.invites.enabled,
    trigger_stat_key:
      typeof raw.trigger_stat_key === "string"
        ? raw.trigger_stat_key
        : DEFAULT_CONFIG.invites.trigger_stat_key,
    trigger_value:
      wholeCount(raw.trigger_value) ?? DEFAULT_CONFIG.invites.trigger_value,
    invitee_reward_name:
      typeof raw.invitee_reward_name === "string"
        ? raw.invitee_reward_name
        : DEFAULT_CONFIG.invites.invitee_reward_name,
    inviter_rewards: normalizeInviterRewards(raw.inviter_rewards),
    reward_ttl_seconds:
      ttlSeconds(raw.reward_ttl_seconds) ??
      DEFAULT_CONFIG.invites.reward_ttl_seconds,
  };
  if (isReward(raw.invitee_reward)) out.invitee_reward = raw.invitee_reward;
  return out;
}

/**
 * Coerce `invites.inviter_rewards` into steps — SHOWING what the server would
 * refuse rather than repairing it.
 *
 * The server reads a ladder all or nothing (internal/invite/config.go
 * parseLadder): one bad step and it pays nobody. So a broken step is kept, for
 * its row to paint red; dropping or fixing it here would draw a working ladder
 * over a server that pays nothing. A number of the wrong type comes in blank,
 * and so does an absent `to` — which the server, too, reads as broken rather
 * than as "no end". A reward that is not an object or a list is dropped by the
 * rule above, and the row says the step needs one.
 *
 * An entry that is not an object at all is junk and goes, as a type row's does.
 */
function normalizeInviterRewards(input: unknown): InviterRewardStep[] {
  if (!Array.isArray(input)) return [];
  const out: InviterRewardStep[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const entry = raw as Partial<Record<keyof InviterRewardStep, unknown>>;
    out.push({
      from: typeof entry.from === "number" ? entry.from : undefined,
      to:
        entry.to === null || typeof entry.to === "number" ? entry.to : undefined,
      name: typeof entry.name === "string" ? entry.name : "",
      reward: isReward(entry.reward) ? entry.reward : undefined,
    });
  }
  return out;
}

/**
 * Server-side defaults that the UI falls back to when a field is unset on the
 * loaded config.
 *
 * The mockup is cited by KEY rather than by line number. The numbers this block
 * used to carry had drifted some fifty lines out of date, and one of them named
 * a value the mockup does not draw at all — a reference nobody can check costs
 * more than no reference. In `docs/mockups/project-config.html`,
 * `chat.max_message_length` reads 200, `guild.max_members` 30,
 * `help_request.default_ttl_seconds` 3600,
 * `rate_limits.auth_per_ip_per_minute` 30 and
 * `rate_limits.help_create_per_minute` 5.
 *
 * Where the mockup and the server disagree the SERVER wins — it is the thing
 * that enforces the number, and a box showing anything else makes the wrong
 * number true on the next save. `help_create_per_minute` is exactly that case:
 * the mockup says 5, the platform enforces 10.
 */
export const DEFAULT_CONFIG: ProjectConfig = {
  chat: {
    history_window: 500,
    max_message_length: 200,
    profanity_words: [],
  },
  guild: {
    max_members: 30,
    require_unique_name: false,
    metadata_schema: [],
    recommendations: {
      requirement_guild_key: "",
      requirement_player_key: "",
    },
    succession_level_key: "",
  },
  help_request: {
    types: [],
    // Seven days, as the design document puts it — long enough that a player
    // who takes a week off still finds what the guild gave them.
    unclaimed_ttl_seconds: 604800,
    default_ttl_seconds: 3600,
  },
  gift: {
    // Both allow-lists start EMPTY, and that is the section's whole default:
    // a project that never configured gifts has them off, rather than quietly
    // acquiring a mechanic — and a send it never enabled cannot be the first
    // its players hear of the feature.
    channels: [],
    types: [],
    // Seven days, as the Help section's unclaimed lifetime already is: long
    // enough that a player who takes a week off still finds what was sent to
    // them, and one number for an operator to learn rather than two.
    unclaimed_ttl_seconds: 604800,
  },
  // Off, with no trigger, no reward and no ladder — a project that never asked
  // for referrals has none, and every one of these keys reads as "not
  // configured" to the server. `reward_ttl_seconds: 0` is the section's one
  // default that is not an absence: it is the ∞ the server reads as «never
  // burns», which is the safe end for a reward nobody has come back for yet.
  invites: {
    enabled: false,
    trigger_stat_key: "",
    trigger_value: 0,
    invitee_reward_name: "",
    inviter_rewards: [],
    reward_ttl_seconds: 0,
  },
  // All eight read back from internal/tenant/ratelimits.go, which is what the
  // server falls back to when the key is absent. A default in this file that
  // disagrees is worse than no default at all: the box shows a number the
  // platform is not enforcing, and saving the form makes the wrong one true.
  // `auth_per_ip_per_minute` said 60 here against the server's 30 for exactly
  // that long.
  rate_limits: {
    global_per_game_per_minute: 1000,
    auth_per_ip_per_minute: 30,
    chat_per_player_per_minute: 20,
    help_create_per_minute: 10,
    friend_requests_per_minute: 10,
    // The same bucket size the help-create limiter beside it uses: both are a
    // brake on a script, and neither is the game rule.
    gifts_per_minute: 10,
    // internal/tenant/ratelimits.go DefaultCloudSavePerMinute.
    cloud_save_per_minute: 10,
    // internal/tenant DefaultInvitesPerMinute — the friend-request bucket's
    // size, since accepting a code is the same kind of act.
    invites_per_minute: 10,
  },
  auth: {
    facebook: {
      enabled: false,
      app_id: "",
      app_secret: "",
    },
  },
  profile: {
    stats_schema: [],
    meta_schema: [],
    allowed_avatars: [],
  },
  // Off, at the server's 10 MiB — internal/cloudsave/config.go. A project
  // that never configured saves has none, rather than quietly acquiring a
  // bucket flow on a version upgrade.
  cloud_save: {
    enabled: false,
    max_bytes: 10485760,
  },
  guild_assets: {
    allowed_logos: [],
  },
  request_signing: {
    // Where every project starts: nothing is verified and nothing is counted.
    // The studio presses log itself, looks at its own numbers, and presses
    // enforce itself — the platform never moves this for anyone.
    mode: "off",
    clock_skew_sec: 300,
  },
};

/**
 * Coerce an unknown blob into a ProjectConfig, filling missing keys from the
 * default. Used on initial load so the form always has a complete shape to
 * bind against even if the backend returns partial config.
 */
export function normalizeConfig(input: unknown): ProjectConfig {
  const root = (input ?? {}) as Partial<Record<keyof ProjectConfig, unknown>>;
  const chat = (root.chat ?? {}) as Partial<ProjectConfig["chat"]>;
  const guild = (root.guild ?? {}) as Partial<ProjectConfig["guild"]>;
  const help = (root.help_request ?? {}) as Partial<
    ProjectConfig["help_request"]
  >;
  const gift = (root.gift ?? {}) as Partial<ProjectConfig["gift"]>;
  const invites = (root.invites ?? {}) as Partial<ProjectConfig["invites"]>;
  const rate = (root.rate_limits ?? {}) as Partial<
    ProjectConfig["rate_limits"]
  >;
  const auth = (root.auth ?? {}) as Partial<ProjectConfig["auth"]>;
  const facebook = (auth.facebook ?? {}) as Partial<
    ProjectConfig["auth"]["facebook"]
  >;
  const profile = (root.profile ?? {}) as Partial<ProjectConfig["profile"]>;
  const cloudSave = (root.cloud_save ?? {}) as Partial<
    ProjectConfig["cloud_save"]
  >;
  const guildAssets = (root.guild_assets ?? {}) as Partial<
    ProjectConfig["guild_assets"]
  >;
  const signing = (root.request_signing ?? {}) as Partial<
    ProjectConfig["request_signing"]
  >;

  return {
    chat: {
      history_window:
        typeof chat.history_window === "number"
          ? chat.history_window
          : DEFAULT_CONFIG.chat.history_window,
      max_message_length:
        typeof chat.max_message_length === "number"
          ? chat.max_message_length
          : DEFAULT_CONFIG.chat.max_message_length,
      profanity_words: Array.isArray(chat.profanity_words)
        ? chat.profanity_words.filter((w): w is string => typeof w === "string")
        : [],
    },
    guild: {
      max_members:
        typeof guild.max_members === "number"
          ? guild.max_members
          : DEFAULT_CONFIG.guild.max_members,
      require_unique_name:
        typeof guild.require_unique_name === "boolean"
          ? guild.require_unique_name
          : DEFAULT_CONFIG.guild.require_unique_name,
      metadata_schema: Array.isArray(guild.metadata_schema)
        ? (guild.metadata_schema as ProjectConfig["guild"]["metadata_schema"])
        : [],
      recommendations: normalizeRecommendations(guild.recommendations),
      // Same reasoning as the recommendation keys: the sub-key is missing on
      // every config written before this feature existed, and a hand-edited
      // blob can carry a number or null where a key belongs. Anything that
      // isn't a string degrades to "" — "rank by service alone".
      succession_level_key:
        typeof guild.succession_level_key === "string"
          ? guild.succession_level_key
          : "",
    },
    // All three durations of this section are read by ONE rule — `ttlSeconds`,
    // the panel's copy of the server's own reader. A stored `-1` is "never" to
    // the server, so it has to arrive here as the `0` the ∞ toggle writes: left
    // as -1 it would fail the minute floor, and that failure gates Save for the
    // WHOLE page — a project the server treats as eternal could not change any
    // other setting. A value that is not a whole number is not a duration to
    // the server either, and falls back to the same platform default it does.
    help_request: {
      types: normalizeHelpTypes(help.types),
      unclaimed_ttl_seconds:
        ttlSeconds(help.unclaimed_ttl_seconds) ??
        DEFAULT_CONFIG.help_request.unclaimed_ttl_seconds,
      default_ttl_seconds:
        ttlSeconds(help.default_ttl_seconds) ??
        DEFAULT_CONFIG.help_request.default_ttl_seconds,
    },
    // Missing on every config written before this section existed, which is
    // the state the defaults describe: no channel, no type, gifts off. The
    // section is read here rather than left out because a key this page never
    // writes back is a key the next Raw JSON save DELETES — see
    // ProjectConfigPage's handleRawChange.
    gift: {
      channels: normalizeGiftChannels(gift.channels),
      types: normalizeGiftTypes(gift.types),
      // Read the way the server reads a duration, exactly as the Help
      // section's are: above zero it is a length of time, at or below zero it
      // is the «never» the ∞ toggle writes, and anything that is not a whole
      // number is not a duration at all and falls back to the platform's own.
      unclaimed_ttl_seconds:
        ttlSeconds(gift.unclaimed_ttl_seconds) ??
        DEFAULT_CONFIG.gift.unclaimed_ttl_seconds,
    },
    // Missing on every config written before this section existed, and read
    // here for the reason the gift one above is: a key this page never writes
    // back is a key the next Raw JSON save DELETES.
    invites: normalizeInvites(invites),
    rate_limits: {
      global_per_game_per_minute:
        typeof rate.global_per_game_per_minute === "number"
          ? rate.global_per_game_per_minute
          : DEFAULT_CONFIG.rate_limits.global_per_game_per_minute,
      auth_per_ip_per_minute:
        typeof rate.auth_per_ip_per_minute === "number"
          ? rate.auth_per_ip_per_minute
          : DEFAULT_CONFIG.rate_limits.auth_per_ip_per_minute,
      chat_per_player_per_minute:
        typeof rate.chat_per_player_per_minute === "number"
          ? rate.chat_per_player_per_minute
          : DEFAULT_CONFIG.rate_limits.chat_per_player_per_minute,
      help_create_per_minute:
        typeof rate.help_create_per_minute === "number"
          ? rate.help_create_per_minute
          : DEFAULT_CONFIG.rate_limits.help_create_per_minute,
      friend_requests_per_minute:
        typeof rate.friend_requests_per_minute === "number"
          ? rate.friend_requests_per_minute
          : DEFAULT_CONFIG.rate_limits.friend_requests_per_minute,
      gifts_per_minute:
        typeof rate.gifts_per_minute === "number"
          ? rate.gifts_per_minute
          : DEFAULT_CONFIG.rate_limits.gifts_per_minute,
      cloud_save_per_minute:
        typeof rate.cloud_save_per_minute === "number"
          ? rate.cloud_save_per_minute
          : DEFAULT_CONFIG.rate_limits.cloud_save_per_minute,
      invites_per_minute:
        typeof rate.invites_per_minute === "number"
          ? rate.invites_per_minute
          : DEFAULT_CONFIG.rate_limits.invites_per_minute,
    },
    auth: {
      facebook: {
        enabled:
          typeof facebook.enabled === "boolean"
            ? facebook.enabled
            : DEFAULT_CONFIG.auth.facebook.enabled,
        app_id:
          typeof facebook.app_id === "string"
            ? facebook.app_id
            : DEFAULT_CONFIG.auth.facebook.app_id,
        app_secret:
          typeof facebook.app_secret === "string"
            ? facebook.app_secret
            : DEFAULT_CONFIG.auth.facebook.app_secret,
      },
    },
    profile: {
      stats_schema: Array.isArray(profile.stats_schema)
        ? (profile.stats_schema as ProjectConfig["profile"]["stats_schema"])
        : [],
      meta_schema: normalizeMetaSchema(profile.meta_schema),
      allowed_avatars: Array.isArray(profile.allowed_avatars)
        ? profile.allowed_avatars.filter(
            (s): s is string => typeof s === "string",
          )
        : [],
    },
    // Missing on every config written before the section existed, and read
    // here for the reason the gift section is: a key this page never writes
    // back is a key the next Raw JSON save DELETES. Bytes are kept as bytes;
    // the MiB the form shows is the page's own arithmetic.
    cloud_save: {
      enabled:
        typeof cloudSave.enabled === "boolean"
          ? cloudSave.enabled
          : DEFAULT_CONFIG.cloud_save.enabled,
      max_bytes:
        typeof cloudSave.max_bytes === "number"
          ? cloudSave.max_bytes
          : DEFAULT_CONFIG.cloud_save.max_bytes,
    },
    guild_assets: {
      allowed_logos: Array.isArray(guildAssets.allowed_logos)
        ? guildAssets.allowed_logos.filter(
            (s): s is string => typeof s === "string",
          )
        : [],
    },
    // Read back exactly as written, unlike every field above it. A word that is
    // not a mode and a window that is not a number are kept and shown, because
    // this is the one section whose form is also the place those mistakes get
    // fixed: coercing them here would rewrite the operator's blob on the next
    // save and hide the very thing the helper text is there to name.
    //
    // «Written» includes shapes no control can show — `true`, `{}`, a number
    // past int64. Those are exactly the values the server reports as overruled,
    // so replacing them with the default would leave the form insisting the
    // default is what the blob asked for. A key that is absent is a different
    // thing and does take the default.
    request_signing: {
      mode:
        typeof signing.mode === "string"
          ? signing.mode
          : DEFAULT_CONFIG.request_signing.mode,
      clock_skew_sec:
        signing.clock_skew_sec === undefined
          ? DEFAULT_CONFIG.request_signing.clock_skew_sec
          : signing.clock_skew_sec,
    },
  };
}
